// src/services/activity.ts
// Monitoring aktywności ucznia: gra zapisuje tu każdą próbę, moduł odkłada próby do kolejki w przeglądarce i wysyła
// je paczkami. Minuty, odsetek i progi liczy backend — front podaje surowe pomiary. Zasady i kontrakt z backendem:
// docs/plan-aktywnosci.md
import { BASE_API_URL } from './ApiService';
import { cleanTextForTTS } from '../utils/textBracketSanitize';

// Przełącznik monitoringu. Wyłączony: gry niczego nie mierzą ani nie odkładają, a nauczyciel nie widzi linijek, ikony
// ani okna „Aktywność”. Uczeń nie widzi nic w żadnym stanie — o monitoringu mówi mu polityka prywatności. Włączony
// wszędzie, także na produkcji. Tylko do testów w `npm start`: `process.env.NODE_ENV === 'development'`.
export const ACTIVITY_ENABLED = true;

// Nazwy gier w danych. Backend trzyma po nich ustawienia (limity czasu, Memory poza odsetkiem, Powtórz poza
// odpowiedziami), więc nie wolno ich zmieniać ani wyliczać z adresu strony — każda gra podaje swoją wprost. Nazwę,
// której nie zna, backend przyjmie z domyślnym limitem i policzy jako odpowiedź, więc nowa gra najpierw w backendzie
export enum ActivityGame {
  REPEAT = 'repeat',
  REPEAT_EASY = 'repeat-easy',
  FIND = 'find',
  FIND_EASY = 'find-easy',
  PICK = 'pick',
  PICK_EASY = 'pick-easy',
  PAIRING = 'pairing',
  PAIRING_EASY = 'pairing-easy',
  MEMORY = 'memory',
  MEMORY_EASY = 'memory-easy',
  RECOGNIZE = 'recognize',
  RECOGNIZE_EASY = 'recognize-easy',
  RECOGNIZE2 = 'recognize2',
  RECOGNIZE2_EASY = 'recognize2-easy',
  WRITING = 'writing',
  WRITING_EASY = 'writing-easy',
  DRAG_LETTERS_EASY = 'drag-letters-easy',
  LETTERS = 'letters',
  LETTERS_EASY = 'letters-easy',
}

// Jedna próba w formacie backendu. Nowe pole tylko po uzgodnieniu i najpierw na backendzie — API odrzuca
// nieznane pola (400 na całą paczkę)
type Answer = {
  id: string;
  listId: number;
  game: ActivityGame;
  // `null` — gra nie zna wyniku (samoocena w grach 6 i 7, Sparuj) albo nie ma w niej odpowiedzi (Powtórz)
  correct: boolean | null;
  answeredAt: string;
  // Od poprzedniej próby w tej grze, a pierwsza — od otwarcia gry
  responseMs: number;
  // Ta część `responseMs`, w której karta była schowana albo okno nie miało fokusu
  awayMs: number;
  // Liczba znaków hasła bez dopisków w nawiasach; nie idzie tylko z Memory
  answerLength?: number;
};

export type ActivitySession = {
  game: ActivityGame;
  listId: number;
  studentId: number;
};

const QUEUE_KEY_PREFIX = 'activityQueue:';
// Tyle prób zbiera się najwyżej przed wysłaniem paczki; reszta idzie co FLUSH_DELAY_MS. Nauczycielowi świeże dane
// nie są potrzebne — wysyłka pilnuje tylko, żeby próby krótko czekały w przeglądarce. Większość i tak wychodzi przy
// wyjściu z gry, schowaniu karty, zamknięciu strony i wylogowaniu
const BATCH_SIZE = 50;
// Limit backendu na jedną paczkę
const MAX_BATCH = 100;
// Także odstęp ponowienia po nieudanej wysyłce
const FLUSH_DELAY_MS = 2 * 60 * 1000;
// Starszych prób backend nie przyjmie
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Przy długiej pracy bez sieci kolejka nie zapcha pamięci przeglądarki — najstarsze próby wypadają
const MAX_QUEUE = 5000;

let session: ActivitySession | null = null;
// Uczeń i token z logowania albo z ostatnio otwartej gry. Kolejka idzie tylko z tym tokenem, także po wyjściu z gry —
// na wspólnym komputerze próby jednego ucznia nie pójdą z kontem innego
let owner: { studentId: number; token: string } | null = null;
let lastMark = 0;
// Od kiedy karta jest schowana albo okno bez fokusu; czas poza aplikacją zbiera się w awayMs do najbliższej próby
let awaySince: number | null = null;
let awayMs = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

// UUID v4. Bez `crypto.randomUUID` (starsze przeglądarki) — z losowych bajtów
const uuid = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const hex = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  const variant = (8 + (parseInt(hex[16], 16) % 4)).toString(16);

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20)}`;
};

const queueKey = (studentId: number) => `${QUEUE_KEY_PREFIX}${studentId}`;

// Kolejka ucznia bez prób starszych niż 7 dni
const readQueue = (studentId: number): Answer[] => {
  try {
    const queue: Answer[] = JSON.parse(localStorage.getItem(queueKey(studentId)) || '[]');
    const oldestAllowed = Date.now() - MAX_AGE_MS;

    return queue.filter((answer) => Date.parse(answer.answeredAt) > oldestAllowed);
  } catch {
    return [];
  }
};

const writeQueue = (studentId: number, queue: Answer[]) => {
  try {
    if (queue.length) {
      localStorage.setItem(queueKey(studentId), JSON.stringify(queue.slice(-MAX_QUEUE)));
    } else {
      localStorage.removeItem(queueKey(studentId));
    }
  } catch { /* pamięć przeglądarki pełna albo zablokowana — próby przepadają */ }
};

const isAway = () => document.visibilityState === 'hidden' || !document.hasFocus();

const setAway = (away: boolean) => {
  if (away && awaySince === null) {
    awaySince = performance.now();
  } else if (!away && awaySince !== null) {
    awayMs += performance.now() - awaySince;
    awaySince = null;
  }
};

// Wysyłka za FLUSH_DELAY_MS; naraz czeka najwyżej jeden termin
const sendLater = (send: () => void) => {
  if (flushTimer) return;

  flushTimer = setTimeout(() => {
    flushTimer = null;
    send();
  }, FLUSH_DELAY_MS);
};

// Wysyła najstarsze próby z kolejki, najwyżej MAX_BATCH. `keepalive` tylko przy chowaniu strony: przeglądarka
// dokończy wtedy wysyłkę po zamknięciu karty. Co nie wyjdzie, czeka w kolejce na następną okazję
const flush = async (keepalive = false): Promise<void> => {
  if (sending || !owner) return;

  const { studentId, token } = owner;

  // Po wylogowaniu albo zalogowaniu na inne konto kolejka czeka, aż ten uczeń znów się zaloguje
  if (localStorage.getItem('token') !== token) return;

  const batch = readQueue(studentId).slice(0, MAX_BATCH);

  if (!batch.length) return;

  sending = true;
  // 0 — brak odpowiedzi serwera (sieć)
  let status = 0;

  try {
    const response = await fetch(`${BASE_API_URL}/v1/students/@me/answers`, {
      method: 'POST',
      keepalive,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sentAt: new Date().toISOString(), answers: batch }),
    });

    status = response.status;
  } catch { /* brak sieci — kolejna próba później */ }

  sending = false;

  // Sesja skończona: kolejka czeka na ponowne zalogowanie tego samego ucznia. Kto zalogował się w trakcie tej wysyłki,
  // ma już nowy token — odmowa starego go nie dotyczy, a jego kolejka idzie od razu
  if (status === 401) {
    if (owner?.token === token) {
      owner = null;
    } else {
      flush();
    }

    return;
  }

  // Brak sieci, awaria serwera, limit żądań albo backend bez tej funkcji (404, np. po powrocie na `master`) —
  // kolejna próba później; kolejka i tak wygasa po 7 dniach
  if (!status || status >= 500 || status === 404 || status === 408 || status === 429) {
    sendLater(flush);

    return;
  }

  // Przyjęte (2xx) albo odrzucone (400 i pozostałe 4xx) — ponowne wysłanie nic nie da, paczka znika z kolejki
  if (status >= 400) {
    console.error(`Monitoring aktywności: serwer odrzucił paczkę prób (${status})`);
  }

  const sentIds = new Set(batch.map((answer) => answer.id));
  const rest = readQueue(studentId).filter((answer) => !sentIds.has(answer.id));

  writeQueue(studentId, rest);

  if (rest.length >= BATCH_SIZE) {
    flush();
  } else if (rest.length) {
    sendLater(flush);
  }
};

// Gra otwarta przez ucznia: od tej chwili liczy się czas pierwszej próby. Resztki kolejki z poprzednich wizyt
// idą od razu
export const startGame = (game: ActivityGame, listId: number, studentId: number): ActivitySession | null => {
  const token = localStorage.getItem('token');

  if (!token) return null;

  session = { game, listId, studentId };
  owner = { studentId, token };
  lastMark = performance.now();
  awayMs = 0;
  awaySince = isAway() ? lastMark : null;
  flush();

  return session;
};

// Wyjście z gry: kolejka idzie od razu. Sesja gry otwartej w międzyczasie zostaje
export const endGame = (ended: ActivitySession | null) => {
  if (session === ended) {
    session = null;
  }
  flush();
};

// Uczeń zalogowany — zaraz po logowaniu albo przy otwarciu aplikacji na jego koncie: kolejka z poprzednich wizyt idzie
// od razu. Bez tego próby, których wysyłka się nie udała, czekałyby, aż uczeń wejdzie do gry
export const sendActivityAfterLogin = (studentId: number) => {
  const token = localStorage.getItem('token');

  if (!token) return;

  owner = { studentId, token };
  flush();
};

// Wylogowanie: kolejka idzie od razu, póki jest token. Wysyłka przy wyjściu z gry przychodzi już po jego usunięciu,
// więc bez tego ostatnie próby czekałyby, aż ten uczeń zaloguje się tu ponownie
export const sendActivityBeforeLogout = () => {
  flush();
};

// Jedna próba w otwartej grze. `correct` — wynik sprawdzony przez grę, `null` — gra go nie zna; `term` — hasło, którego
// próba dotyczy (bez niego nie idzie answerLength, jak w Memory). Poza grą otwartą przez ucznia nic nie robi
export const recordAnswer = (correct: boolean | null, term?: string) => {
  if (!session) return;

  const mark = performance.now();

  if (awaySince !== null) {
    awayMs += mark - awaySince;
    awaySince = mark;
  }

  const responseMs = Math.round(mark - lastMark);
  const answer: Answer = {
    id: uuid(),
    listId: session.listId,
    game: session.game,
    correct,
    answeredAt: new Date().toISOString(),
    responseMs,
    awayMs: Math.min(Math.round(awayMs), responseMs),
  };

  if (term !== undefined) {
    answer.answerLength = cleanTextForTTS(term).length;
  }

  lastMark = mark;
  awayMs = 0;

  const queue = [...readQueue(session.studentId), answer];

  writeQueue(session.studentId, queue);

  // Pełna paczka idzie od razu. Dłuższa kolejka znaczy, że wysyłka się nie udała albo jeszcze trwa — wtedy czeka
  // na termin, żeby przy awarii serwera żądanie nie szło z każdą odpowiedzią
  if (queue.length === BATCH_SIZE) {
    flush();
  } else {
    sendLater(flush);
  }
};

if (ACTIVITY_ENABLED) {
  // Własne zdarzenia okna — blur i focus elementów strony nie wychodzą poza nie
  window.addEventListener('blur', (event) => {
    if (event.target === window) setAway(true);
  });
  window.addEventListener('focus', (event) => {
    if (event.target === window) setAway(document.visibilityState === 'hidden');
  });
  document.addEventListener('visibilitychange', () => {
    setAway(isAway());
    // Schowana karta może już nie wrócić (zamknięcie, telefon usypia kartę w tle) — kolejka idzie od razu
    if (document.visibilityState === 'hidden') {
      flush(true);
    }
  });
  window.addEventListener('pagehide', () => {
    flush(true);
  });
}
